import { all, call, put, takeLatest, select, type SagaReturnType } from 'redux-saga/effects';
import actions from './actions';
import { ERROR_MESSAGES } from '@/constants/events';
import { getConfigs } from '@/features/configs/configs';
import { getStrings } from '@/features/configs/strings';
import orgActions from '@/redux/organization/actions';
import { getOrigin } from '@/utils';
import { prepareErrorMessage } from '@/utils/global';
import { retryWithBackoff } from '@/utils/requests';
import * as ErrorReporter from '@/utils/errorReporter';
import { type RootState } from '@/types/app';

// TEAM-14725: retry each config fetch under the same saga-layer policy as
// fetchLocationSaga. `yield*` runs sequentially, so to keep the two fetches parallel
// they go through `call()` inside the `all()` below — which needs a plain generator per
// fetch (these thin wrappers) rather than a bare `call(retryWithBackoff, …)`.
function* getStringsWithRetry(...args: Parameters<typeof getStrings>) {
  return yield* retryWithBackoff(getStrings, {}, ...args);
}

function* getConfigsWithRetry(...args: Parameters<typeof getConfigs>) {
  return yield* retryWithBackoff(getConfigs, {}, ...args);
}

export function* fetchConfigsSaga(action: ReturnType<typeof actions.fetchConfigs>) {
  const state: RootState = yield select();
  const organization = state.app.organization?.organization;

  try {
    const origin = getOrigin(window.location.host);

    // Fetch configs and strings on both server and client. TEAM-14725: each call is
    // wrapped in retryWithBackoff (same saga-layer policy as fetchLocationSaga) so a
    // transient network hiccup (Load failed / Failed to fetch, or a retryable status)
    // is retried before the saga reports failure, instead of failing on the first blip.
    const [strings, webConfig]: [
      SagaReturnType<typeof getStrings>,
      SagaReturnType<typeof getConfigs>,
    ] = yield all([
      call(getStringsWithRetry, action.queryClient, origin, organization.id),
      call(getConfigsWithRetry, action.queryClient, origin, organization.id),
    ]);

    yield all([
      put(actions.fetchConfigsSuccess(action.clientOrServer, 'webConfig', webConfig)),
      put(actions.fetchConfigsSuccess(action.clientOrServer, 'strings', strings)),
      // @ts-expect-error `organizationConfig` is mistyped.
      put(orgActions.fetchOrganizationSuccess(organization)),
    ]);
  } catch (error) {
    yield all([
      put(actions.fetchConfigsFailure(action.clientOrServer)),
      put(orgActions.fetchOrganizationFail()),
    ]);
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      null,
      error,
    );
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.FETCH_BATCH_CONFIGS_ERROR,
      errorResponse,
    });
  }
}

export default function* rootSaga() {
  yield takeLatest(actions.FETCH_CONFIGS, fetchConfigsSaga);
}
